import './assets/css/App.css';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import AuthLayout from './components/layouts/auth';
import AdminLayout from './components/layouts/admin';
import RTLLayout from './components/layouts/rtl';
import OnboardingWizard from './pages/onboarding/OnboardingWizard';
import { CalendarPreview } from './pages/preview/CalendarPreview';
import { ChakraProvider, Flex, Spinner } from '@chakra-ui/react';
import initialTheme from './theme/theme';
import { useState, useEffect } from 'react';
import { getUserRole, getRoleRedirect } from './utils/auth';
import { useCurrentDbUser } from './hooks/useCurrentUser';
import { VerificationCodeInputPreview } from 'pages/preview/VerificationCodeInputPreview';
import StepsPreview from 'pages/preview/StepsPreview';
import ModalEpisodioPreview from 'pages/preview/ModalEpisodioPreview';
import EvaPainScalePreview from 'pages/preview/EvaPainScalePreview';
import DrawerPreview from 'pages/preview/DrawerPreview';
import DropDownPreview from 'pages/preview/DropDownPreview';
import SCountryPreview from 'pages/preview/SCountryPreview';
import MiniCalendarPreview from 'pages/preview/MiniCalendarPreview';
import InfoBox from 'components/ui/InfoBox';
import InfoBoxPreview from 'pages/preview/InfoBoxPreview';
import EpisodeCreatePreview from 'pages/preview/EpisodeCreatePreview';
import EpisodeCardPreview from 'pages/preview/EpisodeCardPreview';
import TreatmentPlansPreview from 'pages/preview/TreatmentPlansPreview';
import ExerciseForm from 'features/treatments/components/ExerciseForm';
import ExerciseFormPreview from 'pages/preview/ExerciseFormPreview';
import TimeLinePreview from 'pages/preview/TimeLinePreview';
import ClinicalEpisodeCardPreview from 'pages/preview/ClinicalEpisodeCardPreview';
import EpisodePagePreview from 'pages/preview/EpisodePagePreview';
import AvailabilityConfigPreview from 'pages/preview/AvailabilityConfigPreview';
import AppointmentSchedulingPreview from 'pages/preview/AppointmentSchedulingPreview';

function DevTokenLogger(): null {
  const { isSignedIn, getToken } = useAuth();

  useEffect(() => {
    if (!isSignedIn) return;
    getToken().then((token) => {
      console.log('%c[DEV] Clerk JWT:', 'color: #6C47FF; font-weight: bold;', token);
    });
  }, [isSignedIn, getToken]);

  return null;
}

function RootRedirect() {
  const { isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();

  if (!isLoaded) {
    return (
      <Flex minH='100vh' align='center' justify='center'>
        <Spinner size='xl' color='brand.500' />
      </Flex>
    );
  }

  if (isSignedIn && user) {
    const role = getUserRole(user);
    return <Navigate to={getRoleRedirect(role)} replace />;
  }

  return <Navigate to='/auth/sign-in' replace />;
}

function ProtectedRoute({ children }: { children: JSX.Element }) {
  const { isSignedIn, isLoaded } = useAuth();

  if (!isLoaded) return null;
  if (!isSignedIn) return <Navigate to='/auth/sign-in' replace />;
  return children;
}

const CLINICAL_ROLES = ['medico', 'fisioterapeuta', 'pasante'];

function AdminGuardedLayout(props: { theme: any; setTheme: any }) {
  const { data: user, isLoading } = useCurrentDbUser();

  if (isLoading) {
    return (
      <Flex h='100vh' align='center' justify='center'>
        <Spinner size='xl' color='brand.500' />
      </Flex>
    );
  }

  if (user && CLINICAL_ROLES.includes(user.role) && !user.hasAvailability) {
    return <Navigate to='/onboarding/disponibilidad' replace />;
  }

  return <AdminLayout theme={props.theme} setTheme={props.setTheme} />;
}

function GuestRoute({ children }: { children: JSX.Element }) {
  const { isSignedIn, isLoaded } = useAuth();
  const { user } = useUser();

  if (!isLoaded) return null;

  if (isSignedIn && user) {
    const role = getUserRole(user);
    return <Navigate to={getRoleRedirect(role)} replace />;
  }

  return children;
}

export default function Main() {
  const [currentTheme, setCurrentTheme] = useState(initialTheme);

  return (
    <ChakraProvider theme={currentTheme}>
      <DevTokenLogger />
      <Routes>
        <Route
          path='auth/*'
          element={
            <GuestRoute>
              <AuthLayout />
            </GuestRoute>
          }
        />
        {/* Onboarding — accessible to all authenticated users, no sidebar */}
        <Route
          path='onboarding/disponibilidad'
          element={
            <ProtectedRoute>
              <OnboardingWizard />
            </ProtectedRoute>
          }
        />

        <Route path='preview/verification-code' element={<VerificationCodeInputPreview />} />

        <Route
          path='admin/*'
          element={
            <ProtectedRoute>
              <AdminGuardedLayout theme={currentTheme} setTheme={setCurrentTheme} />
            </ProtectedRoute>
          }
        />
        <Route
          path='rtl/*'
          element={
            <ProtectedRoute>
              <RTLLayout theme={currentTheme} setTheme={setCurrentTheme} />
            </ProtectedRoute>
          }
        />
        <Route path='preview/calendar' element={<CalendarPreview />} />
        <Route path='preview/mini-calendar' element={<MiniCalendarPreview />} />
        <Route path='preview/steps' element={<StepsPreview />} />
        <Route path='preview/modal-episodio' element={<ModalEpisodioPreview />} />
         <Route path='preview/eva-pain-scale' element={<EvaPainScalePreview />} />
        <Route path='preview/drawer' element={<DrawerPreview />} /> 
        <Route path='preview/dropdown' element={<DropDownPreview />} /> 
        <Route path='preview/country' element={<SCountryPreview />} />
        <Route path='preview/info-box' element={<InfoBoxPreview />} />
        <Route path='preview/episode-create' element={<EpisodeCreatePreview />} /> 
        <Route path='preview/episode-card' element={<EpisodeCardPreview />} /> 
        <Route path='preview/treatment-plans' element={<TreatmentPlansPreview />} /> 
        <Route path='preview/exercise-form' element={<ExerciseFormPreview planId='' />} />
        <Route path='preview/timeline' element={<TimeLinePreview />} />
        <Route path='preview/clinical-episode-card' element={<ClinicalEpisodeCardPreview />} />
        <Route path='preview/episode-page' element={<EpisodePagePreview />} />
        <Route path='preview/availability-config' element={<AvailabilityConfigPreview />} />
        <Route path='preview/appointment-scheduling' element={<AppointmentSchedulingPreview />} />
        <Route path='/' element={<RootRedirect />} />
      </Routes>
    </ChakraProvider>
  );
}
